import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import ApiClient from "../utils/api";
import {
  Search,
  MoreVertical,
  Trash2,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  ChevronDown,
  Users,
  MessageSquare
} from "lucide-react";
import DeleteConfirmModal from "../components/DeleteConfirmModal";

export default function Contacts({ activeAiAgentId }) {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [totalContacts, setTotalContacts] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination & Search States
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Selection state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [activeMenuId, setActiveMenuId] = useState(null);

  const [deleteModalConfig, setDeleteModalConfig] = useState({
    isOpen: false,
    title: "Delete Contact",
    message: "Are you sure you want to delete this contact? This action is permanent and cannot be undone.",
    onConfirm: () => {}
  });

  const channelOptions = [
    { value: "", label: "All Channels" },
    { value: "email", label: "Email" },
    { value: "freshchat", label: "Freshchat" },
    { value: "freshdesk", label: "Freshdesk" },
    { value: "slack", label: "Slack" },
    { value: "telegram", label: "Telegram" },
    { value: "webchat", label: "Webchat" },
    { value: "webhook", label: "Webhook" },
    { value: "whatsapp", label: "WhatsApp" },
    { value: "widget", label: "Widget Chat" },
  ];

  const [isChannelDropdownOpen, setIsChannelDropdownOpen] = useState(false);
  const channelDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (channelDropdownRef.current && !channelDropdownRef.current.contains(event.target)) {
        setIsChannelDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const aiAgentId = activeAiAgentId || ApiClient.currentAiAgentId;

  // Reset page and selection when agent changes
  useEffect(() => {
    setCurrentPage(1);
    setSelectedIds(new Set());
    setActiveMenuId(null);
  }, [aiAgentId]);

  const [selectedChannels, setSelectedChannels] = useState([]);

  const fetchContacts = useCallback(async (page = currentPage, search = debouncedSearch, channels = selectedChannels.join(",")) => {
    setLoading(true);
    setError(null);
    try {
      const response = await ApiClient.getContacts(page, itemsPerPage, search, channels);
      setContacts(response?.contacts || []);
      setTotalContacts(response?.total_count || 0);
    } catch (err) {
      console.error("Error fetching contacts:", err);
      setError("Failed to load contacts");
    } finally {
      setLoading(false);
    }
  }, [currentPage, debouncedSearch, selectedChannels, itemsPerPage]);

  // Fetch contacts when page, search queries, or channels change
  useEffect(() => {
    fetchContacts(currentPage, debouncedSearch, selectedChannels.join(","));
  }, [aiAgentId, currentPage, debouncedSearch, selectedChannels, fetchContacts]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleRowClick = (e, contactId) => {
    // Don't navigate if clicking checkbox, actions menu, links
    if (
      e.target.closest('.col-checkbox') ||
      e.target.closest('.col-actions') ||
      e.target.closest('.custom-checkbox') ||
      e.target.closest('.email-link')
    ) {
      return;
    }
    navigate(`/contacts/${contactId}`);
  };

  // Selection handlers
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = contacts.map(c => c.id);
      setSelectedIds(new Set(allIds));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectRow = (id) => {
    const nextSelected = new Set(selectedIds);
    if (nextSelected.has(id)) {
      nextSelected.delete(id);
    } else {
      nextSelected.add(id);
    }
    setSelectedIds(nextSelected);
  };

  const handleDeleteClick = (contactId) => {
    setDeleteModalConfig({
      isOpen: true,
      title: "Delete Contact",
      message: "Are you sure you want to delete this contact? This action is permanent and cannot be undone.",
      onConfirm: async () => {
        try {
          await ApiClient.deleteContact(contactId);
          setDeleteModalConfig(prev => ({ ...prev, isOpen: false }));
          if (contacts.length === 1 && currentPage > 1) {
            setCurrentPage(p => p - 1);
          } else {
            fetchContacts(currentPage, debouncedSearch);
          }
        } catch (err) {
          console.error("Error deleting contact:", err);
        }
      }
    });
    setActiveMenuId(null);
  };

  const handleBulkDelete = () => {
    if (selectedIds.size === 0) return;
    setDeleteModalConfig({
      isOpen: true,
      title: "Delete Selected Contacts",
      message: `Are you sure you want to delete the ${selectedIds.size} selected contacts? This action is permanent and cannot be undone.`,
      onConfirm: async () => {
        setLoading(true);
        try {
          await Promise.all(Array.from(selectedIds).map(id => ApiClient.deleteContact(id)));
          setSelectedIds(new Set());
          setDeleteModalConfig(prev => ({ ...prev, isOpen: false }));
          fetchContacts(currentPage, debouncedSearch);
        } catch (err) {
          console.error("Error bulk deleting contacts:", err);
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const startItem = totalContacts === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalContacts);
  const totalPages = Math.ceil(totalContacts / itemsPerPage);

  const getChannelBadge = (chanRaw) => {
    const chan = String(chanRaw || 'widget').toLowerCase();
    let label = 'Widget Chat';
    let bg = 'bg-blue-50 text-blue-700 border-blue-200/60';

    if (chan === 'webchat') {
      label = 'Webchat';
      bg = 'bg-indigo-50 text-indigo-700 border-indigo-200/60';
    } else if (chan === 'whatsapp') {
      label = 'WhatsApp';
      bg = 'bg-emerald-50 text-emerald-700 border-emerald-200/60';
    } else if (chan === 'telegram') {
      label = 'Telegram';
      bg = 'bg-sky-50 text-sky-700 border-sky-200/60';
    } else if (chan === 'slack') {
      label = 'Slack';
      bg = 'bg-purple-50 text-purple-700 border-purple-200/60';
    } else if (chan === 'email') {
      label = 'Email';
      bg = 'bg-amber-50 text-amber-700 border-amber-200/60';
    } else if (chan === 'freshdesk') {
      label = 'Freshdesk';
      bg = 'bg-emerald-50 text-emerald-700 border-emerald-200/60';
    } else if (chan === 'freshchat') {
      label = 'Freshchat';
      bg = 'bg-teal-50 text-teal-700 border-teal-200/60';
    } else if (chan === 'webhook') {
      label = 'Webhook';
      bg = 'bg-rose-50 text-rose-700 border-rose-200/60';
    } else if (chan === 'zendesk') {
      label = 'Zendesk';
      bg = 'bg-orange-50 text-orange-700 border-orange-200/60';
    }

    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold border ${bg}`}>
        {label}
      </span>
    );
  };

  return (
    <div className="flex flex-col min-h-screen bg-white font-['Inter',sans-serif] animate-slideIn">
      <div className="w-full max-w-7xl mx-auto px-4 py-5 sm:px-6 sm:py-6 lg:px-8 space-y-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-5 sm:pb-6 border-b border-zinc-100 gap-3 sm:gap-4">
          <div>
            <h1 className="text-lg sm:text-xl font-bold text-zinc-900 tracking-tight">Contacts Directory</h1>
            <p className="text-xs text-zinc-500 mt-0.5 sm:mt-1">
              Browse, filter, and inspect users across all integration channels and conversation sessions.
            </p>
          </div>
          {selectedIds.size > 0 && (
            <button
              onClick={handleBulkDelete}
              className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold text-xs shadow-xs cursor-pointer self-start sm:self-auto"
            >
              <Trash2 size={13} />
              <span>Delete Selected ({selectedIds.size})</span>
            </button>
          )}
        </div>


        {/* Toolbar: Search + Multi-Channel Select + Page Stats */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-2 pb-1">
          <div className="flex items-center gap-2.5 flex-wrap flex-1">
            {/* Search Input */}
            <div className="relative w-full sm:w-64">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input
                type="text"
                placeholder="Search all contacts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-1.5 bg-zinc-50 border border-zinc-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-zinc-900 focus:bg-white text-zinc-900 placeholder:text-zinc-400 transition-colors"
              />
            </div>

            {/* Channel Filter Dropdown */}
            <div ref={channelDropdownRef} className="relative">
              <button
                onClick={() => setIsChannelDropdownOpen(!isChannelDropdownOpen)}
                className="inline-flex items-center justify-between gap-2 px-3 py-1.5 bg-zinc-50 border border-zinc-200 rounded-lg text-xs text-zinc-700 hover:bg-zinc-100 transition-colors cursor-pointer min-w-[140px]"
              >
                <span className="truncate">
                  {selectedChannels.length === 0
                    ? "All Channels"
                    : selectedChannels.length === 1
                      ? channelOptions.find((o) => o.value === selectedChannels[0])?.label
                      : `${selectedChannels.length} Channels`}
                </span>
                <ChevronDown
                  size={14}
                  className={`text-zinc-500 transition-transform ${isChannelDropdownOpen ? "rotate-180" : ""}`}
                />
              </button>

              {isChannelDropdownOpen && (
                <div className="absolute top-full left-0 mt-1 w-48 bg-white border border-zinc-200 rounded-xl shadow-lg z-50 p-1.5 space-y-0.5 max-h-60 overflow-y-auto">
                  {channelOptions.map((opt) => {
                    const isSelected = opt.value === "" ? selectedChannels.length === 0 : selectedChannels.includes(opt.value);
                    return (
                      <button
                        key={opt.value}
                        onClick={() => {
                          if (opt.value === "") {
                            setSelectedChannels([]);
                          } else {
                            const next = [...selectedChannels];
                            const idx = next.indexOf(opt.value);
                            if (idx > -1) next.splice(idx, 1);
                            else next.push(opt.value);
                            setSelectedChannels(next);
                          }
                          setCurrentPage(1);
                        }}
                        className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs flex items-center gap-2 transition-colors ${
                          isSelected ? "bg-zinc-100 text-zinc-900 font-semibold" : "text-zinc-600 hover:bg-zinc-50"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          readOnly
                          className="w-3.5 h-3.5 rounded border-zinc-300 text-zinc-900 pointer-events-none"
                        />
                        <span>{opt.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Pagination Counter & Arrows */}
          <div className="flex items-center gap-2 text-xs text-zinc-500 self-end sm:self-auto">
            <span>
              {startItem}–{endItem} of {totalContacts}
            </span>
            <div className="flex items-center gap-1">
              <button
                disabled={currentPage === 1 || loading}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                className="w-7 h-7 flex items-center justify-center border border-zinc-200 rounded-lg text-zinc-600 hover:bg-zinc-50 disabled:opacity-40 disabled:hover:bg-transparent cursor-pointer"
                title="Previous page"
              >
                <ChevronLeft size={14} />
              </button>
              <button
                disabled={currentPage >= totalPages || loading}
                onClick={() => setCurrentPage((p) => p + 1)}
                className="w-7 h-7 flex items-center justify-center border border-zinc-200 rounded-lg text-zinc-600 hover:bg-zinc-50 disabled:opacity-40 disabled:hover:bg-transparent cursor-pointer"
                title="Next page"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </div>

        {/* Contacts Table Card */}
        <div className="bg-white rounded-xl border border-zinc-200/80 shadow-2xs overflow-hidden">
          {error ? (
            <div className="p-12 text-center text-red-500 flex flex-col items-center justify-center gap-2">
              <AlertCircle size={28} />
              <p className="font-semibold text-xs">{error}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-zinc-200/80 bg-zinc-50/50 text-[10px] font-bold text-zinc-400 uppercase tracking-wider">
                    <th className="py-2.5 px-4 w-10 text-center">
                      <input
                        type="checkbox"
                        checked={contacts.length > 0 && selectedIds.size === contacts.length}
                        onChange={handleSelectAll}
                        className="w-3.5 h-3.5 rounded border-zinc-300 text-zinc-900 cursor-pointer"
                      />
                    </th>
                    <th className="py-2.5 px-4 font-semibold">Contact</th>
                    <th className="py-2.5 px-4 font-semibold">Username / User ID</th>
                    <th className="py-2.5 px-4 font-semibold">Channel</th>
                    <th className="py-2.5 px-4 font-semibold">Email</th>
                    <th className="py-2.5 px-4 font-semibold">Phone number</th>
                    <th className="py-2.5 px-4 font-semibold">Location</th>
                    <th className="py-2.5 px-4 w-10 text-right"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100 text-xs">
                  {loading ? (
                    Array.from({ length: 8 }).map((_, i) => (
                      <tr key={`skel-${i}`} className="animate-pulse">
                        <td className="py-3 px-4 text-center">
                          <div className="w-3.5 h-3.5 bg-zinc-200 rounded mx-auto" />
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2.5">
                            <div className="w-7 h-7 rounded-full bg-zinc-200" />
                            <div className="h-3 w-24 bg-zinc-200 rounded" />
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="h-3 w-20 bg-zinc-200 rounded" />
                        </td>
                        <td className="py-3 px-4">
                          <div className="h-4 w-16 bg-zinc-200 rounded" />
                        </td>
                        <td className="py-3 px-4">
                          <div className="h-3 w-28 bg-zinc-200 rounded" />
                        </td>
                        <td className="py-3 px-4">
                          <div className="h-3 w-20 bg-zinc-200 rounded" />
                        </td>
                        <td className="py-3 px-4">
                          <div className="h-3 w-16 bg-zinc-200 rounded" />
                        </td>
                        <td className="py-3 px-4" />
                      </tr>
                    ))
                  ) : contacts.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="py-16 text-center text-zinc-400">
                        <Users size={32} className="mx-auto mb-2 opacity-30 text-zinc-400" />
                        <p className="font-medium text-xs text-zinc-600">No contacts found</p>
                        <p className="text-[11px] text-zinc-400 mt-0.5">
                          {searchTerm || selectedChannels.length > 0
                            ? "Try adjusting your search query or channel filter."
                            : "Contacts will automatically appear here as users engage with your agent."}
                        </p>
                      </td>
                    </tr>
                  ) : (
                    contacts.map((contact) => {
                      const isSelected = selectedIds.has(contact.id);

                      let locationVal = "--";
                      if (contact.custom_parameters) {
                        try {
                          const params = typeof contact.custom_parameters === 'string'
                            ? JSON.parse(contact.custom_parameters)
                            : contact.custom_parameters;
                          if (params && params.location) {
                            locationVal = params.location;
                          }
                        } catch (e) {}
                      }

                      return (
                        <tr
                          key={contact.id}
                          onClick={(e) => handleRowClick(e, contact.id)}
                          className={`transition-colors hover:bg-zinc-50/70 cursor-pointer ${
                            isSelected ? "bg-zinc-50/50" : ""
                          }`}
                        >
                          {/* Checkbox */}
                          <td className="py-3 px-4 text-center col-checkbox">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleSelectRow(contact.id)}
                              onClick={(e) => e.stopPropagation()}
                              className="w-3.5 h-3.5 rounded border-zinc-300 text-zinc-900 cursor-pointer custom-checkbox"
                            />
                          </td>

                          {/* Contact Name & Avatar */}
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2.5">
                              <div className="relative w-7 h-7 flex-shrink-0">
                                {(() => {
                                  let params = {};
                                  try {
                                    params = typeof contact.custom_parameters === 'string'
                                      ? JSON.parse(contact.custom_parameters)
                                      : contact.custom_parameters;
                                  } catch (e) {}

                                  let imgUrl = "";
                                  if (params?.avatar_url) {
                                    const apiBase = process.env.REACT_APP_API_BASE_URL || '';
                                    const baseDomain = apiBase.endsWith('/api') ? apiBase.slice(0, -4) : apiBase;
                                    imgUrl = `${baseDomain}${params.avatar_url}`;
                                  } else {
                                    const cSeed = params?.avatar_seed || contact.name || contact.userid || String(contact.id);
                                    imgUrl = `https://api.dicebear.com/7.x/micah/svg?seed=${cSeed}&backgroundColor=b6e3f4,c0aede,d1d4f9`;
                                  }

                                  return (
                                    <img
                                      src={imgUrl}
                                      alt={contact.name}
                                      className="w-full h-full rounded-full object-cover border border-zinc-200"
                                    />
                                  );
                                })()}

                                {contact.is_escalated && (
                                  <div
                                    className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 shadow-2xs"
                                    title="Escalated to human"
                                  >
                                    <AlertCircle size={8} />
                                  </div>
                                )}
                              </div>
                              <span className="font-semibold text-zinc-900">{contact.name}</span>
                            </div>
                          </td>

                          {/* Username / User ID */}
                          <td className="py-3 px-4 font-mono text-[11px] text-zinc-500">
                            {(() => {
                              const uid = contact.userid;
                              if (!uid || uid === 'anonymous' || uid.toLowerCase() === 'value') return 'anonymous';
                              const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
                              if (uuidRegex.test(uid)) return 'anonymous';
                              if (/^(widget|telegram|slack|freshchat|freshdesk|zendesk|email|whatsapp|webchat)_/i.test(uid)) return 'anonymous';
                              return uid;
                            })()}
                          </td>

                          {/* Channel Badge */}
                          <td className="py-3 px-4">
                            {getChannelBadge(contact.custom_parameters?.channel)}
                          </td>

                          {/* Email */}
                          <td className="py-3 px-4 text-zinc-600">
                            {(() => {
                              const em = contact.email;
                              const invalid = !em || em === 'N/A' || em.trim().toLowerCase() === 'value';
                              if (invalid) return <span className="text-zinc-400">--</span>;
                              return (
                                <a
                                  href={`mailto:${em}`}
                                  className="email-link hover:underline hover:text-zinc-900"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {em}
                                </a>
                              );
                            })()}
                          </td>

                          {/* Phone */}
                          <td className="py-3 px-4 text-zinc-600">
                            {(() => {
                              const ph = contact.phone;
                              const invalid = !ph || ph === 'N/A' || ph.trim().toLowerCase() === 'value';
                              return invalid ? <span className="text-zinc-400">--</span> : ph;
                            })()}
                          </td>

                          {/* Location */}
                          <td className="py-3 px-4 text-zinc-600">
                            {locationVal === "--" ? <span className="text-zinc-400">--</span> : locationVal}
                          </td>

                          {/* Actions */}
                          <td className="py-3 px-4 text-right col-actions relative">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setActiveMenuId(activeMenuId === contact.id ? null : contact.id);
                              }}
                              className="p-1 rounded-md text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 transition-colors cursor-pointer"
                              aria-label="Actions"
                            >
                              <MoreVertical size={14} />
                            </button>

                            {activeMenuId === contact.id && (
                              <>
                                <div
                                  className="fixed inset-0 z-40"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveMenuId(null);
                                  }}
                                />
                                <div
                                  className="absolute right-4 top-8 w-36 bg-white border border-zinc-200 rounded-xl shadow-lg z-50 p-1 space-y-0.5"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <button
                                    onClick={() => {
                                      if (contact.sessions && contact.sessions.length > 0) {
                                        navigate(`/analytics?tab=conversations&session_id=${contact.sessions[0].session_id}`);
                                      } else {
                                        navigate(`/analytics?tab=conversations`);
                                      }
                                      setActiveMenuId(null);
                                    }}
                                    className="w-full text-left px-2 py-1.5 text-xs text-zinc-700 hover:bg-zinc-100 rounded-lg flex items-center gap-1.5 transition-colors cursor-pointer"
                                  >
                                    <MessageSquare size={13} />
                                    <span>View Chats</span>
                                  </button>
                                  <div className="border-t border-zinc-100 my-0.5" />
                                  <button
                                    onClick={() => {
                                      handleDeleteClick(contact.id);
                                      setActiveMenuId(null);
                                    }}
                                    className="w-full text-left px-2 py-1.5 text-xs text-red-600 hover:bg-red-50 rounded-lg flex items-center gap-1.5 transition-colors cursor-pointer"
                                  >
                                    <Trash2 size={13} />
                                    <span>Delete Contact</span>
                                  </button>
                                </div>
                              </>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Delete Contact Modal */}
      <DeleteConfirmModal
        isOpen={deleteModalConfig.isOpen}
        onClose={() => setDeleteModalConfig((prev) => ({ ...prev, isOpen: false }))}
        onConfirm={deleteModalConfig.onConfirm}
        title={deleteModalConfig.title}
        message={deleteModalConfig.message}
        confirmText="Delete Permanently"
      />
    </div>
  );
}
